import React, { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import AuthImage from "@/components/AuthImage";

const FIELDS = [
  { key: "title", label: "Job title", placeholder: "Head of Operations" },
  { key: "department", label: "Department", placeholder: "Operations" },
  { key: "company", label: "Company", placeholder: "Renova Eco Systems" },
  { key: "phone", label: "Phone", placeholder: "+1 555 123 4567" },
  { key: "work_email", label: "Work email", placeholder: "name@renova.com" },
  { key: "website", label: "Website", placeholder: "renova.com" },
  { key: "linkedin", label: "LinkedIn", placeholder: "linkedin.com/in/…" },
  { key: "address", label: "Office address", placeholder: "123 Green St, City" },
];

const ImagePicker = ({ label, fileId, onUploaded, round, testid }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <div className="flex items-center gap-3">
      <div className={`${round ? "rounded-full" : "rounded-md"} w-16 h-16 bg-gray-100 border border-gray-200 overflow-hidden flex items-center justify-center shrink-0`}>
        <AuthImage fileId={fileId} className="w-full h-full object-cover" fallback={<Icons.ImagePlus className="w-5 h-5 text-gray-400" />} />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs text-gray-500">{label}</Label>
        <div className="flex gap-2">
          <input ref={inputRef} type="file" accept="image/*" hidden onChange={pick} data-testid={`${testid}-input`} />
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`${testid}-btn`}>
            <Icons.Upload className="w-3.5 h-3.5 mr-1.5" />{busy ? "Uploading…" : fileId ? "Replace" : "Upload"}
          </Button>
          {fileId && (
            <Button type="button" size="sm" variant="ghost" className="text-red-500" onClick={() => onUploaded("")} data-testid={`${testid}-remove`}>
              Remove
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Editable business-card form. saveFn(payload) must return { profile }.
export default function BusinessCardForm({ initial = {}, saveFn, onSaved }) {
  const [form, setForm] = useState({
    title: "", department: "", company: "", phone: "", work_email: "",
    website: "", linkedin: "", address: "", avatar_file_id: "", logo_file_id: "",
    ...initial,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await saveFn(form);
      toast.success("Business card saved");
      onSaved?.(data.profile || form);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <ImagePicker label="Profile picture" round fileId={form.avatar_file_id} testid="avatar-upload"
          onUploaded={(id) => set("avatar_file_id", id)} />
        <ImagePicker label="Company logo" fileId={form.logo_file_id} testid="logo-upload"
          onUploaded={(id) => set("logo_file_id", id)} />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <div key={f.key} className="space-y-1.5">
            <Label className="text-xs text-gray-500">{f.label}</Label>
            <Input value={form[f.key] || ""} placeholder={f.placeholder}
              onChange={(e) => set(f.key, e.target.value)} data-testid={`card-field-${f.key}`} />
          </div>
        ))}
      </div>

      <Button onClick={save} disabled={saving} className="bg-[#0A0A0A] hover:bg-gray-800" data-testid="save-card-btn">
        <Icons.Check className="w-4 h-4 mr-1.5" />{saving ? "Saving…" : "Save business card"}
      </Button>
    </div>
  );
}
